import { Fragment, useMemo, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { LoadingPage } from '#packages/web/layouts/page/templates';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';

import { type AuditDetailsPageViewModel } from './audit-details-page.view-model';
import {
  AuditDetailsPageRenderer,
  type AuditDetailsPageLinks,
} from './audit-details-page.renderer';

export type AuditDetailsPageContainerProps =
  ViewModelProps<AuditDetailsPageViewModel> & LoggingContext;

export const AuditDetailsPageContainer: FC<AuditDetailsPageContainerProps> = ({
  viewModel,
}) => {
  const { page, deleteDialog, onDelete } = viewModel;

  const links = useMemo<AuditDetailsPageLinks | undefined>(() => {
    if (page.state !== 'success') {
      return undefined;
    }

    return {
      energyAudit: {
        background: routes.audit.getEnergyAuditBackgroundFormPath(page.data.id),
        mechanical: routes.audit.getEnergyAuditMechanicalFormPath(page.data.id),
        lightingType: routes.audit.getEnergyAuditLightingTypeFormPath(
          page.data.id,
        ),
        lightingCount: routes.audit.getEnergyAuditLightingCountFormPath(
          page.data.id,
        ),
        waterFixture: routes.audit.getEnergyAuditWaterFixtureFormPath(
          page.data.id,
        ),
        windows: routes.audit.getEnergyAuditWindowsFormPath(page.data.id),
        checklist: routes.audit.getEnergyAuditChecklistFormPath(page.data.id),
        ecms: routes.audit.getEnergyAuditEcmsFormPath(page.data.id),
        reviewSubmit: routes.audit.getEnergyAuditReviewSubmitPath(page.data.id),
      },

      auditSettings: routes.audit.getSettingsPath(page.data.id),
    };
  }, [page]);

  return (
    <Fragment>
      {/* Loading */}
      {page.state === 'loading' && <LoadingPage />}

      {/* Success */}
      {page.state === 'success' && <AuditDetailsPageRenderer links={links!} />}

      {/* Error */}
      {page.state === 'error' && <ThrowError error={page.error} />}

      {/* Delete Dialog */}
      {deleteDialog.isOpen && (
        <DeleteConfirmationDialog
          open
          onCancel={deleteDialog.close}
          onConfirm={onDelete.bind(null, { confirmed: true })}
        />
      )}
    </Fragment>
  );
};
